import { db, now } from '../db/dexie';
import type { Attachment, GlobalTag, Item, List, ListTag } from '../db/types';
import { newestOf, readCursor, writeCursor } from './cursor';
import { syncBlobs } from './blobs';
import { currentUserId, isOffline, supabase } from './supabase';

export interface SyncResult {
  pulled: number;
  pushed: number;
  uploaded: number;
  downloaded: number;
  conflicts: number;
}

export type SyncPhase = 'idle' | 'syncing' | 'offline' | 'error' | 'signed-out' | 'disabled';

/** A row as it exists on the server: everything except the local dirty flag. */
type Remote<T> = Omit<T, 'dirty'>;

/** Shape shared by all three synced tables. */
interface Syncable {
  id: string;
  updated_at: string;
  dirty: 0 | 1;
}

/**
 * Columns that must never be sent to the server.
 *
 * `updated_at` is set by the Postgres trigger. PC and phone clocks drift, and
 * last-write-wins with skewed clocks silently loses edits — so the server's
 * clock is the only clock that orders anything.
 *
 * `dirty` and `uploaded` exist only in the Dexie schema. They are this
 * device's private bookkeeping about what it still owes the server, and there
 * are no such columns in Postgres to receive them.
 *
 * `tags` is the retired v1 string array. The column still exists server-side
 * and is `not null default '{}'`, but this client no longer owns it — the v3
 * upgrade replaced it with list_tag_ids and global_tag_ids.
 */
const STRIP = ['dirty', 'updated_at', 'uploaded', 'tags'] as const;

type TableName = 'items' | 'lists' | 'list_tags' | 'global_tags' | 'attachments';

/** Dexie's camelCase stores, keyed by the snake_case name Postgres uses. */
const LOCAL_TABLE: Record<TableName, 'items' | 'lists' | 'listTags' | 'globalTags' | 'attachments'> =
  {
    items: 'items',
    lists: 'lists',
    list_tags: 'listTags',
    global_tags: 'globalTags',
    attachments: 'attachments',
  };

/**
 * Foreign keys dictate the order: `items.list_id` and `list_tags.list_id`
 * reference `lists`, and `attachments.item_id` references `items`. Pushing a
 * task before the list it belongs to fails on `items_list_id_fkey` — and
 * because the first failure aborts the cycle, nothing after it syncs either.
 */
const PUSH_ORDER: TableName[] = ['lists', 'list_tags', 'global_tags', 'items', 'attachments'];

/**
 * The same foreign keys, seen from the child's side: which column points at
 * which parent table.
 *
 * Ordering the push is not enough on its own, because the push only sends
 * *dirty* rows. A parent that is clean locally — already synced once — is never
 * sent again, so if its server row disappears (deleted straight from the SQL
 * editor, or restored from a backup taken before it existed) every child of it
 * fails on the foreign key forever, and since the first failure aborts the
 * cycle, nothing else syncs either.
 */
const PARENT_OF: Partial<Record<TableName, { table: TableName; column: string }>> = {
  list_tags: { table: 'lists', column: 'list_id' },
  items: { table: 'lists', column: 'list_id' },
  attachments: { table: 'items', column: 'item_id' },
};

/**
 * One full reconciliation: pull, push, blobs, cursor.
 *
 * Runs on app start, on foreground, after any local write (debounced), and on
 * a timer. Every step is safe to repeat — a cycle that dies halfway leaves the
 * database consistent and the next cycle picks up where it stopped.
 */
export async function runSync(): Promise<SyncResult> {
  const result: SyncResult = { pulled: 0, pushed: 0, uploaded: 0, downloaded: 0, conflicts: 0 };

  const client = supabase;
  if (!client) return result;

  const userId = await currentUserId();
  if (!userId) return result;

  const cursor = await readCursor();

  // ── PULL ────────────────────────────────────────────────
  const pull = async (table: TableName) => {
    const { data, error } = await client
      .from(table)
      .select('*')
      .gt('updated_at', cursor)
      .order('updated_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as Array<{ updated_at: string }>;
  };

  const [items, lists, listTags, globalTags, attachments] = await Promise.all([
    pull('items'),
    pull('lists'),
    pull('list_tags'),
    pull('global_tags'),
    pull('attachments'),
  ]);

  // Same dependency order as the push, so a half-applied pull never leaves an
  // item pointing at a list this device has not seen yet.
  await merge('lists', lists as unknown as Remote<List>[], false);
  await merge('list_tags', listTags as unknown as Remote<ListTag>[], false);
  await merge('global_tags', globalTags as unknown as Remote<GlobalTag>[], false);
  result.conflicts = await merge('items', items as unknown as Remote<Item>[], true);
  await merge(
    'attachments',
    // `uploaded` is a Dexie-only column, so it does not come back from the
    // server. A row that arrived over the wire is by definition already in
    // Storage — without restoring the 1 here it would match neither the upload
    // branch nor the download branch, and its image would never arrive.
    (attachments as unknown as Remote<Attachment>[]).map((a) => ({ ...a, uploaded: 1 as const })),
    false,
  );
  result.pulled =
    items.length + lists.length + listTags.length + globalTags.length + attachments.length;

  // ── PUSH ────────────────────────────────────────────────
  // Sequential, in dependency order — a parallel push would race the foreign keys.
  for (const table of PUSH_ORDER) {
    result.pushed += await push(table, userId);
  }

  // ── BLOBS ───────────────────────────────────────────────
  const blobs = await syncBlobs(userId);
  result.uploaded = blobs.uploaded;
  result.downloaded = blobs.downloaded;

  // ── CURSOR ──────────────────────────────────────────────
  const newest = newestOf([items, lists, listTags, globalTags, attachments]);
  if (newest) await writeCursor(newest);

  return result;
}

/**
 * Merges a pulled batch into Dexie.
 *
 * A locally dirty row always survives the merge, and its own push then decides
 * the outcome — the server stamps `updated_at`, so whichever device pushes
 * last wins. Comparing the local clock against the server's here instead would
 * reintroduce exactly the skew the trigger exists to eliminate.
 */
async function merge<T extends Syncable>(
  name: TableName,
  rows: Remote<T>[],
  recordConflicts: boolean,
): Promise<number> {
  if (rows.length === 0) return 0;
  let conflicts = 0;
  const table = tableFor<T>(name);

  await db.transaction('rw', db[LOCAL_TABLE[name]], db.conflicts, async () => {
    const locals = await table.bulkGet(rows.map((r) => r.id));

    for (const [i, remote] of rows.entries()) {
      const local = locals[i];

      if (local?.dirty === 1) {
        // Both sides moved. Keep the local edit, and leave a record — LWW
        // resolves silently, so this log is the only evidence it happened.
        if (recordConflicts && local.updated_at !== remote.updated_at) {
          conflicts += 1;
          await db.conflicts.add({
            item_id: remote.id,
            at: now(),
            title: (local as unknown as Item).title || '(untitled)',
            local_updated_at: local.updated_at,
            remote_updated_at: remote.updated_at,
            winner: 'local',
            // Keep the losing text so it can be recovered by hand.
            losing_body: (remote as unknown as Item).body ?? '',
          });
        }
        continue;
      }

      // `user_id` is server-side bookkeeping; there is one user, so storing it
      // locally would only add noise to every row and to the JSON export.
      const { user_id: _user_id, ...row } = remote as Remote<T> & { user_id?: string };
      await table.put({ ...row, dirty: 0 } as unknown as T);
    }
  });

  return conflicts;
}

/**
 * The slice of a Dexie table this engine actually touches.
 *
 * Declared structurally rather than reaching for `EntityTable<T, 'id'>`: the
 * engine is generic over three tables that share only these four operations,
 * and Dexie's own generics do not unify across them.
 */
interface SyncTable<T extends Syncable> {
  bulkGet(keys: string[]): Promise<(T | undefined)[]>;
  put(row: T): Promise<unknown>;
  update(key: string, changes: Partial<T>): Promise<unknown>;
  where(index: 'dirty'): { equals(value: number): { toArray(): Promise<T[]> } };
}

const tableFor = <T extends Syncable>(name: TableName) =>
  db[LOCAL_TABLE[name]] as unknown as SyncTable<T>;

/**
 * Upserts every dirty row, then clears the flag — but only for rows that were
 * not touched again while the request was in flight. Clearing unconditionally
 * would drop an edit made during the round trip.
 */
async function push(name: TableName, userId: string): Promise<number> {
  const table = tableFor<Syncable>(name);
  const dirty = await table.where('dirty').equals(1).toArray();
  if (dirty.length === 0) return 0;

  await ensureParents(name, dirty, userId);

  const { error } = await supabase!
    .from(name)
    .upsert(dirty.map((row) => serialise(row, userId)), { onConflict: 'id' });
  if (error) throw error;

  const sent = new Map(dirty.map((row) => [row.id, row.updated_at]));
  await db.transaction('rw', db[LOCAL_TABLE[name]], async () => {
    const current = await table.bulkGet([...sent.keys()]);
    for (const row of current) {
      if (row && row.updated_at === sent.get(row.id)) {
        await table.update(row.id, { dirty: 0 });
      }
    }
  });

  return dirty.length;
}

/**
 * A local row as the server wants it: no local-only columns, plus the owner.
 *
 * Keys holding `undefined` are deleted rather than left in place, because
 * postgrest-js builds its `columns=` list from `Object.keys()` — which counts a
 * key whose value is `undefined` — while `JSON.stringify` drops the value. The
 * column ends up declared with nothing to put in it, so PostgREST writes NULL
 * instead of letting the column default apply, and any `not null` column named
 * that way fails with 23502.
 */
function serialise(row: Syncable, userId: string): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(row as object), user_id: userId };
  for (const column of STRIP) delete out[column];
  for (const key of Object.keys(out)) {
    if (out[key] === undefined) delete out[key];
  }
  return out;
}

/**
 * Upserts the parent rows the batch about to be pushed depends on, so the
 * foreign key cannot fail no matter what state the server is in.
 *
 * Recursive, because the chain is real: an attachment needs its item, and that
 * item needs its list. Sending a clean parent again is harmless — the upsert is
 * idempotent, and the only cost is that the server bumps its `updated_at` and
 * the next pull sees it once more.
 */
async function ensureParents(name: TableName, rows: Syncable[], userId: string): Promise<void> {
  const link = PARENT_OF[name];
  if (!link) return;

  const ids = new Set<string>();
  for (const row of rows) {
    const id = (row as unknown as Record<string, unknown>)[link.column];
    if (typeof id === 'string') ids.add(id);
  }
  if (ids.size === 0) return;

  // A missing parent means the local database is already inconsistent; the
  // child's own push will surface that far more usefully than a crash here.
  const parents = (await tableFor<Syncable>(link.table).bulkGet([...ids])).filter(
    (row): row is Syncable => Boolean(row),
  );
  if (parents.length === 0) return;

  await ensureParents(link.table, parents, userId);

  const { error } = await supabase!
    .from(link.table)
    .upsert(parents.map((row) => serialise(row, userId)), { onConflict: 'id' });
  if (error) throw error;
}

/** Classifies a thrown sync error into something the status indicator can show. */
export function phaseFor(error: unknown): SyncPhase {
  return isOffline(error) ? 'offline' : 'error';
}

/**
 * A readable one-liner for a failed cycle. PostgREST returns the useful part
 * in `message`/`details`/`hint` rather than in the Error prototype, and a bare
 * "sync failed" is useless when the cause is a named constraint.
 */
export function describeError(error: unknown): string {
  if (error && typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string };
    const parts = [e.message, e.details, e.hint].filter(Boolean);
    if (parts.length) return `${e.code ? `${e.code}: ` : ''}${parts.join(' — ')}`;
  }
  return error instanceof Error ? error.message : String(error);
}
